"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import {
  splitCorrectScoresListToSections,
  formatCorrectScoresForGrid,
} from "@/utils/matchInfo";
import { LIVE_STATUSES } from "@/contants";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import DuOtherBetsDropdown from "../DuOtherBetsDropdown/DuOtherBetsDropdown";
import DuKbvMatchMinute from "../DuKbvMatchMinute";
import DuOdds3 from "./DuOdds3";
import ViuGoTipsAiModal from "../ViuGoTipsAiModal";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function DuMatch({ match, leagueName, index }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match.status);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeCorner,
    currentAwayCorner,
    homeYellow,
    awayYellow,
    homeRed,
    awayRed,
  } = useMatchData(liveScore, match);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const ftHandicap = (
    <DuOdds3
      left={homeHandicapOdds}
      middle={handicapValue}
      right={awayHandicapOdds}
      leftTrend={homeHandicapTrend}
      rightTrend={awayHandicapTrend}
    />
  );
  const ftOu = (
    <DuOdds3
      left={overOdds}
      middle={overUnderValue}
      right={underOdds}
      leftTrend={overTrend}
      rightTrend={underTrend}
    />
  );
  const ftEurope = (
    <DuOdds3
      left={homeEuropeOdds}
      middle={instantDrawOdds}
      right={awayEuropeOdds}
      leftTrend={homeEuropeTrend}
      middleTrend={drawTrend}
      rightTrend={awayEuropeTrend}
    />
  );

  const h1Handicap = (
    <DuOdds3
      left={homeHandicapHalfOdds}
      middle={handicapHalfValue}
      right={awayHandicapHalfOdds}
      leftTrend={homeHandicapHalfTrend}
      rightTrend={awayHandicapHalfTrend}
    />
  );
  const h1Ou = (
    <DuOdds3
      left={overHalfOdds}
      middle={overUnderHalfValue}
      right={underHalfOdds}
      leftTrend={overHalfTrend}
      rightTrend={underHalfTrend}
    />
  );
  const h1Europe = (
    <DuOdds3
      left={homeEuropeHalfOdds}
      middle={drawHalfOdds}
      right={awayEuropeHalfOdds}
      leftTrend={homeEuropeHalfTrend}
      middleTrend={drawHalfTrend}
      rightTrend={awayEuropeHalfTrend}
    />
  );

  const correctScoresListFull = useMemo(
    () =>
      otherOddsData?.correctScoresOdds
        ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
        : [],
    [otherOddsData],
  );
  const correctScoresFull = useMemo(
    () => splitCorrectScoresListToSections(correctScoresListFull),
    [correctScoresListFull],
  );
  const totalCornersOdds = useMemo(
    () =>
      Array.isArray(otherOddsData?.totalCornersOdds)
        ? otherOddsData.totalCornersOdds
        : [],
    [otherOddsData],
  );
  const hasOtherData = useMemo(
    () =>
      otherOddsData?.goalOddEvenOdds?.length > 0 ||
      correctScoresListFull.length > 0 ||
      totalCornersOdds.length > 0,
    [otherOddsData, correctScoresListFull.length, totalCornersOdds.length],
  );

  const handleOtherClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData && match?.id) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight) return;

    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match?.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const otherOddsCount = Number(match?.countOtherOdds ?? 0);
  const formattedGio = match?.matchTime ? dayjs(match.matchTime).format("HH:mm") : "--:--";
  const formattedDate = match?.matchTime ? dayjs(match.matchTime).format("DD/MM") : "--/--";
  const derivedLeagueName = match?.league?.name || match?.leagueName || leagueName || "";
  const derivedLeagueLogo = match?.league?.logo || match?.leagueLogo || "";
  const scoreText = `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`;
  const modalStatusLabel = !isLive ? "Sắp diễn ra" : "Trực tiếp";

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(!!mql.matches);
    apply();
    if (mql.addEventListener) mql.addEventListener("change", apply);
    else mql.addListener(apply);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", apply);
      else mql.removeListener(apply);
    };
  }, []);

  return (
    <>
      {/* Desktop row */}
      {isDesktop ? (
      <div
        className="flex flex-col border-b border-white/10 py-3"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.10)" }}
      >
        {/* Odds grid (2 rows: TT + H1) */}
        <div className="grid grid-cols-[264px_1fr_1fr_1fr] grid-rows-2 items-center gap-x-5">
          {/* Match info (rowspan 2: TT + H1) */}
          <div className="row-span-2 pl-4">
            <div className="flex h-full gap-2">
              <div style={{ borderRight: "1px solid rgba(255,255,255,0.10)" }}>
                <DuKbvMatchMinute
                  matchTime={match?.matchTime}
                  status={statusNum}
                  liveScore={liveScore}
                  halfStartTime={match?.halfStartTime}
                  className={`flex h-14 w-[56px] flex-col items-center justify-start pr-2 pt-1 text-xs font-medium ${
                    isLive
                      ? "du-odds-up"
                      : "text-[var(--theme-text-muted)]"
                  }`}
                  showDate
                  stacked
                />
              </div>

              <div
                className="flex flex-1 pr-2"
                style={{ borderRight: "1px solid rgba(255,255,255,0.10)" }}
              >
                {/* Teams block + separate FT/HT column */}
                <div className="flex min-w-0 flex-1">
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <img
                        className="h-6 w-6 rounded-full bg-white/10 object-contain"
                        alt=""
                        src={homeLogo || ""}
                      />
                      <div className="truncate text-left text-sm font-semibold text-[var(--theme-text)]">
                        {match?.homeName}
                      </div>
                    </div>
                    <div className="flex min-w-0 items-center gap-2 mt-2">
                      <img
                        className="h-6 w-6 rounded-full bg-white/10 object-contain"
                        alt=""
                        src={awayLogo || ""}
                      />
                      <div className="truncate text-left text-sm font-semibold text-[var(--theme-text)]">
                        {match?.awayName}
                      </div>
                    </div>
                  </div>

                  <div
                    className="ml-2 mt-1 w-8 shrink-0 pl-2 text-center text-xs font-semibold text-[var(--theme-text-muted)]"
                    style={{ borderLeft: "1px solid rgba(255,255,255,0.10)" }}
                  >
                    <div className="pt-1">FT</div>
                    <div className="mt-4">HT</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 1: TT */}
          <div className="w-full">{ftHandicap}</div>
          <div className="w-full">{ftOu}</div>
          <div className="w-full grid place-items-center">{ftEurope}</div>

          {/* Row 2: H1 */}
          <div className="w-full">{h1Handicap}</div>
          <div className="w-full">{h1Ou}</div>
          <div className="w-full grid place-items-center">{h1Europe}</div>
        </div>

        {/* Action row (desktop): Tips left, Other right */}
        <div className="flex items-center justify-between px-4 pt-2">
          <button
            type="button"
            onClick={handleOpenTipsAiModal}
            className="kbv-tips-btn-indigo-slate relative flex h-[26px] w-max max-w-full shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-[8px] border border-solid p-[3px] text-[11px] font-medium leading-[15px] text-white max-lg:w-max lg:h-auto lg:gap-1 lg:px-3 lg:py-2 lg:text-[12px]"
          >
            <img
              src={viugoTipsAiSparkle}
              alt=""
              width={16}
              height={16}
              className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4"
              aria-hidden
            />
            <div>Tips AI</div>
          </button>

          <button
            type="button"
            onClick={handleOtherClick}
            className="h-[36px] w-[100px] rounded-sm bg-[var(--theme-highlight)] px-2.5 py-1 text-xs font-bold uppercase text-[var(--theme-on-primary)] transition hover:brightness-95"
          >
            Kèo khác {otherOddsCount}+
          </button>
        </div>
      </div>
      ) : null}

      {/* Mobile rows */}
      {!isDesktop ? (
      <div>
        {/* Match info row */}
        <button
          type="button"
          className="grid w-full grid-cols-[38px_auto_50px] items-start gap-x-2 border-0 border-b border-white/10 bg-transparent py-2 text-left shadow-none outline-none ring-0 focus:outline-none focus-visible:outline-none rounded-none"
          style={{
            border: 0,
            borderBottom: "1px solid rgba(255,255,255,0.10)",
            backgroundColor: "transparent",
            boxShadow: "none",
            outline: "none",
            borderRadius: 0,
          }}
        >
          <div className="pt-0.5 text-center">
            <div className="text-xs font-medium text-[var(--theme-text-muted)]">
              {dayjs(match?.matchTime).format("HH:mm")}
            </div>
            <div className="text-xs font-medium text-[var(--theme-text-muted)]">
              {dayjs(match?.matchTime).format("DD/MM")}
            </div>
          </div>

          <div className="grid gap-y-1">
            <div className="flex items-center gap-2">
              <div className="grid h-6 w-6 place-items-center overflow-hidden rounded-full bg-white/10">
                <img className="h-4 w-4 object-contain" alt="" src={homeLogo || ""} />
              </div>
              <div className="line-clamp-1 text-left text-sm font-semibold text-[var(--theme-text)]">
                {match?.homeName}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="grid h-6 w-6 place-items-center overflow-hidden rounded-full bg-white/10">
                <img className="h-4 w-4 object-contain" alt="" src={awayLogo || ""} />
              </div>
              <div className="line-clamp-1 text-left text-sm font-semibold text-[var(--theme-text)]">
                {match?.awayName}
              </div>
            </div>
          </div>

          <div className="pt-2 text-right text-xs font-semibold text-[var(--theme-text-muted)]" />
        </button>

        {/* FT / HT labels */}
        <div className="grid w-full grid-cols-[120px_56px_120px] items-center gap-x-2 pt-2 text-[11px] font-semibold text-[var(--theme-text-muted)] md:grid-cols-[158px_auto_158px]">
          <div className="grid grid-cols-3 gap-0 ml-6">
            <div />
            <div className="text-center pl-1.5">FT</div>
            <div />
          </div>
          <div />
          <div className="grid grid-cols-3 gap-0  ml-6">
            <div />
            <div className="text-center pl-1.5">HT</div>
            <div />
          </div>
        </div>

        {/* Odds: Chấp */}
        <button
          type="button"
          className="grid w-full grid-cols-[120px_56px_120px] items-center gap-x-2 border-0 border-b border-white/10 bg-transparent py-2 shadow-none outline-none ring-0 focus:outline-none focus-visible:outline-none rounded-none md:grid-cols-[158px_auto_158px]"
          style={{
            border: 0,
            borderBottom: "1px solid rgba(255,255,255,0.10)",
            backgroundColor: "transparent",
            boxShadow: "none",
            outline: "none",
            borderRadius: 0,
          }}
        >
          {ftHandicap}
          <div className="text-center text-sm font-medium text-[var(--theme-text)]">Chấp</div>
          {h1Handicap}
        </button>

        {/* Odds: 1x2 */}
        <button
          type="button"
          className="grid w-full grid-cols-[120px_56px_120px] items-center gap-x-2 border-0 border-b border-white/10 bg-transparent py-2 shadow-none outline-none ring-0 focus:outline-none focus-visible:outline-none rounded-none md:grid-cols-[158px_auto_158px]"
          style={{
            border: 0,
            borderBottom: "1px solid rgba(255,255,255,0.10)",
            backgroundColor: "transparent",
            boxShadow: "none",
            outline: "none",
            borderRadius: 0,
          }}
        >
          {ftEurope}
          <div className="text-center text-sm font-medium text-[var(--theme-text)]">1x2</div>
          {h1Europe}
        </button>

        {/* Odds: Tài xỉu */}
        <button
          type="button"
          className="grid w-full grid-cols-[120px_56px_120px] items-center gap-x-2 border-0 border-b border-white/10 bg-transparent py-2 shadow-none outline-none ring-0 focus:outline-none focus-visible:outline-none rounded-none md:grid-cols-[158px_auto_158px]"
          style={{
            border: 0,
            borderBottom: "1px solid rgba(255,255,255,0.10)",
            backgroundColor: "transparent",
            boxShadow: "none",
            outline: "none",
            borderRadius: 0,
          }}
        >
          {ftOu}
          <div className="text-center text-sm font-medium text-[var(--theme-text)]">Tài xỉu</div>
          {h1Ou}
        </button>

        {/* Buttons */}
        <div className="flex items-center gap-2 pb-3 pt-2">
          <button
            type="button"
            onClick={handleOpenTipsAiModal}
            className="kbv-tips-btn-indigo-slate relative flex h-[32px] w-max max-w-full shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-[8px] border border-solid p-[3px] text-[11px] font-medium leading-[15px] text-white max-lg:w-max lg:h-auto lg:gap-1 lg:px-3 lg:py-2 lg:text-[12px]"
          >
            <img
              src={viugoTipsAiSparkle}
              alt=""
              width={16}
              height={16}
              className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4"
              aria-hidden
            />
            <div>Tips AI</div>
          </button>
          <button
            className="h-auto min-h-8 w-full flex-1 rounded-sm bg-[var(--theme-highlight)] py-1 text-sm font-bold uppercase text-[var(--theme-on-primary)] transition hover:brightness-95"
            type="button"
            onClick={handleOtherClick}
          >
            Kèo khác {otherOddsCount}+
          </button>
        </div>
      </div>
      ) : null}

      {showOther ? (
        <DuOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{
            ...filterOdds,
            homeEuropeOdds,
            awayEuropeOdds,
            instantDrawOdds,
            homeEuropeTrend,
            awayEuropeTrend,
            drawTrend,
            homeHandicapHalfOdds,
            awayHandicapHalfOdds,
            homeHandicapHalfTrend,
            awayHandicapHalfTrend,
          }}
          showOther={showOther}
        />
      ) : null}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{
          formattedDate,
          formattedGio,
          isLive,
        }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={derivedLeagueName}
        leagueLogo={derivedLeagueLogo}
        homeName={match?.homeName}
        homeLogo={homeLogo}
        awayName={match?.awayName}
        awayLogo={awayLogo}
        matchDate={formattedDate}
        matchTime={formattedGio}
        matchTimestamp={match?.matchTime}
        headerScore={scoreText}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={modalStatusLabel}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </>
  );
}

